import React, { useEffect, useState, useMemo } from "react";
import "../WorkOrders/WorkOrder.css";
import "./InstallationJob.css";
import mainLogo from "../../../images/smoodi_Logo.png";
import { useThemeMode } from "../../../_metronic/partials";
import { Formik } from "formik";
import {
  InstalltionFormSteps,
  useInstallationForm,
} from "../../providers/InstallationFormProvider";
import { MachineSetup } from "./Tabs/MachineSetup";
import { MachineData } from "./Tabs/MachineData";
import CreateTicket from "./Tabs/CreateTicket";
import LocationInformation from "./Tabs/LocationInformation";
import LocationSelector from "./Tabs/LocationSelector";
import {
  GetLocationsType,
  updateInstallationJob,
} from "../../models/_publicCall";
import PictureAndVideo from "./Tabs/PictureAndVideo";
import InstallationJobPreview from "./Tabs/InstallationJobPreview";
import LocationContacts from "./Tabs/LocationContacts";
import toast, { Toaster } from "react-hot-toast";
import { v4 as uuidv4 } from "uuid";
import { useParams, useNavigate } from "react-router-dom";
import { InstallationJobStepsList } from "./InstallationSteps";
import { completeInstallationJob } from "../../models/_publicCall";
export const INSTALLATION_JOB_STORAGE_KEY = "installationJob";
export const INSTALLATION_JOB_STEP_STORAGE_KEY = "installationJobCurrentStep";
function InstallationFormOutline() {
  const { mode } = useThemeMode();
  const { id } = useParams(); // Get the ID from the route parameters
  const {
    loading,
    machinesDB,
    locationsDB,
    issueRepotedDB,
    ticketTypes,
    machineServicesDB,
    materialDB,
    headerDB,
    flavors,
    ledRingsDB,
    categoriesDB,
    diagnosis,
    fetchInstallationJob,
    installationJobDB,
    videoOptions,
    fetchVideos,
    blendersDB,
    tubeShieldsDB,
    cupHolderDB,
    boardsDB,
    refetchBlenders,
    refetchBoards,
    refetchTubeShields,
    refetchCupHolders,
    refetchLedRings,
  } = useInstallationForm();

  const [currentStep, setCurrentStep] = useState<number>();
  const navigate = useNavigate();
  const [selectedLocation, setSelectedLocation] =
    useState<GetLocationsType | null>(null);
  const [selectedMachineForTicket, setSelectedMachineForTicket] =
    useState<any>(null);
  const [showJobCompleted, setShowJobCompleted] = useState(false);
  const [isMachineSetupChangesCompleted, setIsMachineSetupChangesCompleted] =
    useState(false);
  const [installationJobStepsRequired, setInstallationJobStepsRequired] =
    useState<{ id: number; label: string }[]>(InstallationJobStepsList);
  const contactTypeDB = [
    { id: "PERSONAL", name: "Personal" },
    { id: "OFFICE", name: "Office" },
  ];

  const [generatedJobId] = useState(() => uuidv4());
  const jobId = useMemo(() => id || generatedJobId, [id, generatedJobId]);
  const currentStepIndex = InstallationJobStepsList.findIndex(
    (s) => s.id === currentStep,
  );

  const [storedData, setStoredData] = useState<any>({});
  const [isFormSubmitted, setIsFormSubmitted] = useState<boolean>(false);

  const handleLocationChange = (locationId: string) => {
    const location = locationsDB.find((l) => l.id === locationId) || null;
    setSelectedLocation(location);
  };

  const setCurrentStepWithStorage = (step: number) => {
    setCurrentStep(step);
    localStorage.setItem(
      INSTALLATION_JOB_STEP_STORAGE_KEY,
      JSON.stringify(step),
    );
  };

  const installationJobDBStatus: string = installationJobDB
    ? installationJobDB["status"]
    : "";

  useEffect(() => {
    const init = async () => {
      try {
        // Case 1: ID is available → Fetch from API
        if (id) {
          const installationJob = await fetchInstallationJob(id);
          if (!installationJob) return;

          // Load stored data (optional)
          const stored = localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY);
          if (stored) {
            try {
              setStoredData(JSON.parse(stored));
            } catch {
              console.warn("Failed to parse stored installation data");
            }
          } else {
            const initialData = {
              existingInstallationJobId: id,
              locationSelection: {
                id: installationJob.locationId,
                name: installationJob.locationName,
                address: installationJob.locationAddress, // always store current address
                workOrderPdf: installationJob.workOrderPdf,
                locationNumber: installationJob.locationNumber || "",
                headerType: installationJob.headerType || "",
                contractOwner: installationJob.contractOwner || "",
                videoUuid: installationJob.videoUuid || "",
                simCardLast4digits: installationJob.simCardLast4digits || "",
                hasSmoodiRouter: !!installationJob.hasSmoodiRouter,
                hasWifiExtender: !!installationJob.hasWifiExtender,
                hasPressurePump: !!installationJob.hasPressurePump,
                hasPressureTank: !!installationJob.hasPressureTank,
              },
              locationInformation: {
                headerType: installationJob.headerType || "",
                contractOwner: installationJob.contractOwner || "",
                videoUuid: installationJob.videoUuid || "",
                simCardLast4digits: installationJob.simCardLast4digits || "",
                hasSmoodiRouter: !!installationJob.hasSmoodiRouter,
                hasWifiExtender: !!installationJob.hasWifiExtender,
                hasPressurePump: !!installationJob.hasPressurePump,
                hasPressureTank: !!installationJob.hasPressureTank,
                flavorPrices:
                  installationJob.flavorPrice?.map((fp: any) => ({
                    flavor: fp.flavor,
                    price: String(fp.price ?? 0),
                    is_active: fp.is_active,
                  })) || [],
              },
              machineSetup:
                installationJob.machines.map((m) => ({
                  id: m.id,
                  serialNumber: m.serialNumber,
                  currentMachineName: m.currentMachineName,
                  newMachineName: m.newMachineName,
                  deploymentDate: m.deploymentDate,
                })) || [],
            };
            localStorage.setItem(
              INSTALLATION_JOB_STORAGE_KEY,
              JSON.stringify(initialData),
            );
            setStoredData(initialData);
          }

          // Determine current step
          if (installationJob.status === "Completed") {
            setCurrentStepWithStorage(InstalltionFormSteps.SUBMIT);
          } else if (installationJob.status === "In-process") {
            setIsMachineSetupChangesCompleted(true);
            setCurrentStepWithStorage(InstalltionFormSteps.PICTURES_AND_VIDEO);
          } else if (installationJob.hasMachineSetupBeenCompleted) {
            setIsMachineSetupChangesCompleted(true);

            const stepRaw = localStorage.getItem(
              INSTALLATION_JOB_STEP_STORAGE_KEY,
            );
            let step = InstalltionFormSteps.MACHINE_DATA;

            if (stepRaw) {
              try {
                step = Number(JSON.parse(stepRaw));
                if (step < InstalltionFormSteps.MACHINE_DATA) {
                  step = InstalltionFormSteps.MACHINE_DATA;
                }
              } catch {
                console.warn("Failed to parse step from storage");
              }
            }

            setInstallationJobStepsRequired([
              { id: InstalltionFormSteps.MACHINE_DATA, label: "Machine Data" },
              {
                id: InstalltionFormSteps.CREATE_TICKET,
                label: "Create Ticket",
              },
              {
                id: InstalltionFormSteps.LOCATION_INFORMATION,
                label: "Location Information",
              },
              {
                id: InstalltionFormSteps.LOCATION_CONTACTS,
                label: "Location Contacts",
              },
              {
                id: InstalltionFormSteps.PICTURES_AND_VIDEO,
                label: "Location Notes",
              },
              { id: InstalltionFormSteps.SUBMIT, label: "Submit" },
            ]);
            setCurrentStepWithStorage(step);
          } else {
            setInstallationJobStepsRequired([
              {
                id: InstalltionFormSteps.CHOOSE_LOCATION,
                label: "Location Selection",
              },
              {
                id: InstalltionFormSteps.MACHINE_SETUP,
                label: "Machine Setup",
              },
            ]);
            setCurrentStepWithStorage(InstalltionFormSteps.CHOOSE_LOCATION);
          }
        }

        // Case 2: No ID → Load directly from storage
        else {
          const stored = localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY);
          const stepRaw = localStorage.getItem(
            INSTALLATION_JOB_STEP_STORAGE_KEY,
          );
          let step = InstalltionFormSteps.CHOOSE_LOCATION;
          setInstallationJobStepsRequired([
            {
              id: InstalltionFormSteps.CHOOSE_LOCATION,
              label: "Location Selection",
            },
            { id: InstalltionFormSteps.MACHINE_SETUP, label: "Machine Setup" },
          ]);
          if (stored) {
            try {
              const parsedData = JSON.parse(stored);
              if (parsedData?.existingInstallationJobId) {
                localStorage.removeItem(INSTALLATION_JOB_STORAGE_KEY);
                setStoredData({});
                let step = InstalltionFormSteps.CHOOSE_LOCATION;
                setCurrentStepWithStorage(step);
                return;
              }
              setStoredData(parsedData);
            } catch {
              console.warn("Failed to parse stored data");
              setStoredData({});
            }

            if (stepRaw) {
              try {
                step = Number(JSON.parse(stepRaw));
              } catch {
                console.warn("Failed to parse step from storage");
              }
            }
          } else {
            setStoredData({});
          }

          setCurrentStepWithStorage(step);
        }
      } catch (error) {
        console.error("Initialization failed:", error);
      }
    };

    init();
  }, [id]);

  const setToast = (message: string, type: "success" | "error") => {
    if (type === "success") {
      toast.success(message);
    } else {
      toast.error(message);
    }
  };

  return (
    <>
      <div
        className={`wo-body ${mode === "dark" ? "dark-wo-body" : ""}`}
        style={{ padding: "10px !important" }}
      >
        <div className="wo-logo-container">
          <img src={mainLogo} alt="Smoody Logo" className="wo-logo" />
        </div>

        <div className="wo-form-wrapper">
          <div
            className={`wo-header-container ${
              mode === "dark" ? "dark-wo-header-container" : ""
            }`}
          >
            <h2 className="wo-header-title">Installation Job</h2>
          </div>
        </div>
        {!loading && (
          <div className="wo-form-wrapper">
            <div
              className={`wo-form-container ${
                mode === "dark" ? "dark-theme" : ""
              }`}
            >
              <Formik
                initialValues={{
                  locationId: "",
                  locationName: "",
                  locationAddress: "",
                  machines: [],
                  videoUuid: "",
                  headerType: "",
                  contractOwner: "",
                  marketingMaterials: [],
                  flavorPrice: [],
                  hasSmoodiRouter: false,
                  hasWifiExtender: false,
                  hasPressurePump: false,
                  hasPressureTank: false,
                  simCardLast4digits: "",
                  secondContactName: "",
                  secondContactRole: "",
                  secondContactPhone: "",
                  secondContactEmail: "",
                  secondContactPhoneType: "",
                  marketingContactName: "",
                  marketingContactRole: "",
                  marketingContactPhone: "",
                  marketingContactEmail: "",
                  marketingContactPhoneType: "",
                  storeManagerContactName: "",
                  storeManagerContactRole: "",
                  storeManagerContactPhone: "",
                  storeManagerContactEmail: "",
                  storeManagerContactPhoneType: "",
                  itContactName: "",
                  itContactRole: "",
                  itContactPhone: "",
                  itContactEmail: "",
                  itContactPhoneType: "",
                  serviceContactName: "",
                  serviceContactRole: "",
                  serviceContactPhone: "",
                  serviceContactEmail: "",
                  serviceContactPhoneType: "",
                  uploadImagesOrVideos: {
                    imageOfFullSmoodiSetUp: [],
                    setupFreezerDoorPhoto: [],
                    machineBackPhoto: [],
                    machineEntranceViewPhoto: [],
                    storeWalkthroughVideo: [],
                    getStatusCheckImage: [],
                  },
                  additionalComments: "",
                  additionalNoteCategoryId: "",
                }}
                onSubmit={async (_, { setSubmitting }) => {
                  // Read fresh data from localStorage at submission time
                  const freshStoredData = JSON.parse(
                    localStorage.getItem(INSTALLATION_JOB_STORAGE_KEY) || "{}",
                  );

                  const currentLocationContacts =
                    freshStoredData?.locationContactsById?.[
                      freshStoredData?.locationSelection?.id
                    ] || {};
                  const machines = freshStoredData?.machineSetup || [];

                  const allStatusCheckImages = machines.map((machine: any) => {
                    const machineId =
                      machine?.selectedMachine?.id ||
                      machine?.machineId ||
                      machine?.selectedMachineId ||
                      "";
                    const assets = (machine?.statusCheckImage || []).map(
                      (x: any) => ({
                        name: x?.name || "",
                        type: x?.type || "",
                        url: x?.url || x?.content || "",
                      }),
                    );
                    return {
                      machine_id: machineId,
                      machine_assets: assets,
                    };
                  });

                  const $inputData: any = {
                    // --- Location Info ---
                    locationId: freshStoredData?.locationSelection?.id || "",
                    locationName:
                      freshStoredData?.locationSelection?.name || "",
                    locationAddress:
                      freshStoredData?.locationSelection?.address || "",

                    // --- Machines ---
                    machines: (freshStoredData?.machineSetup || []).map(
                      (m: any) => ({
                        id:
                          m?.machineId ||
                          m?.selectedMachine?.id ||
                          m?.selectedMachineId ||
                          "",
                        serialNumber: m?.selectedMachine?.serial_number || "",
                        currentMachineName:
                          m?.selectedMachine?.machine_name || "",
                        newMachineName:
                          m?.newMachineName ||
                          m?.selectedMachine?.machine_name ||
                          "",
                        deploymentDate: m?.deploymentDate || "",
                        made4Smoothies: !!m?.smoothiesMade,
                        passedSanitizerTest: !!m?.sanitizerPassed,
                        getStatusPassed: !!m?.statusCheckPassed,
                        waterLevelCalibratedWithScale: !!m?.waterCalibrated,
                        filterType: m?.filterType || "",
                        machineNotes: m?.notes || "",
                        blenderId: m?.blenderId || "",
                        ledRingId: m?.ledRingId || "",
                        tubeShieldId: m?.tubeShieldId || "",
                        cupHolderId: m?.cupHolderId || "",
                        boardId: m?.boardId || "",
                        ticket: m?.ticketData?.ticketType
                          ? {
                              type: m?.ticketData?.ticketType || "",
                              issueReported: (
                                m?.ticketData?.issuesReported || []
                              ).map((i: any) => i?.id || ""),
                              otherIssueReported:
                                m?.ticketData?.otherIssueReported || "",
                              recommandedServices: (
                                m?.ticketData?.machineServices || []
                              ).map((s: any) => s?.id || ""),
                              otherServices:
                                m?.ticketData?.otherMachineService || "",
                              status: m?.ticketData?.status || "",
                              media: (m?.ticketData?.media || []).map(
                                (x: any) => ({
                                  name: x?.name || "",
                                  url: x?.url || "",
                                  type: x?.type || "",
                                }),
                              ),
                              comments: m?.ticketData?.comment || "",
                            }
                          : {},
                      }),
                    ),

                    // --- Header / Marketing Info ---
                    videoUuid:
                      freshStoredData?.locationInformation?.videoUuid || "",
                    headerType:
                      freshStoredData?.locationInformation?.headerType || "",
                    contractOwner:
                      freshStoredData?.locationInformation?.contractOwner || "",
                    marketingMaterials:
                      freshStoredData?.locationInformation
                        ?.selectedMarketingMaterials || [],
                    flavorPrice: (
                      freshStoredData?.locationInformation?.flavorPrices || []
                    ).map((f: any) => ({
                      flavor: f?.flavor || "",
                      price: parseFloat(f?.price || 0),
                      is_active: f?.is_active ?? true,
                    })),
                    hasSmoodiRouter:
                      !!freshStoredData?.locationInformation?.hasSmoodiRouter,
                    hasWifiExtender:
                      !!freshStoredData?.locationInformation?.hasWifiExtender,
                    hasPressurePump:
                      !!freshStoredData?.locationInformation?.hasPressurePump,
                    hasPressureTank:
                      !!freshStoredData?.locationInformation?.hasPressureTank,
                    simCardLast4digits:
                      freshStoredData?.locationInformation?.simCardLast4digits,
                    // --- Contacts ---
                    customerSecondContact:
                      currentLocationContacts?.hasSecondContact || false,
                    customerMarketingContact:
                      currentLocationContacts?.hasMarketingContact || false,
                    customerItContact:
                      currentLocationContacts?.hasItContact || false,
                    customerServiceContact:
                      currentLocationContacts?.hasServiceContact || false,
                    storeManagerContact:
                      currentLocationContacts?.hasStoreManagerContact || false,

                    // Second Contact
                    secondContactName:
                      currentLocationContacts?.secondContact?.name || "",
                    secondContactRole:
                      currentLocationContacts?.secondContact?.role || "",
                    secondContactPhone:
                      currentLocationContacts?.secondContact?.phone || "",
                    secondContactEmail:
                      currentLocationContacts?.secondContact?.email || "",
                    secondContactPhoneType:
                      currentLocationContacts?.secondContact?.phoneType || "",

                    // Marketing Contact
                    marketingContactName:
                      currentLocationContacts?.marketingContact?.name || "",
                    marketingContactRole:
                      currentLocationContacts?.marketingContact?.role || "",
                    marketingContactPhone:
                      currentLocationContacts?.marketingContact?.phone || "",
                    marketingContactEmail:
                      currentLocationContacts?.marketingContact?.email || "",
                    marketingContactPhoneType:
                      currentLocationContacts?.marketingContact?.phoneType ||
                      "",

                    // IT Contact
                    itContactName:
                      currentLocationContacts?.itContact?.name || "",
                    itContactRole:
                      currentLocationContacts?.itContact?.role || "",
                    itContactPhone:
                      currentLocationContacts?.itContact?.phone || "",
                    itContactEmail:
                      currentLocationContacts?.itContact?.email || "",
                    itContactPhoneType:
                      currentLocationContacts?.itContact?.phoneType || "",

                    // Service Contact
                    serviceContactName:
                      currentLocationContacts?.serviceContact?.name || "",
                    serviceContactRole:
                      currentLocationContacts?.serviceContact?.role || "",
                    serviceContactPhone:
                      currentLocationContacts?.serviceContact?.phone || "",
                    serviceContactEmail:
                      currentLocationContacts?.serviceContact?.email || "",
                    serviceContactPhoneType:
                      currentLocationContacts?.serviceContact?.phoneType || "",

                    // Store Manager Contact
                    storeManagerContactName:
                      currentLocationContacts?.storeManagerContact?.name || "",
                    storeManagerContactPhone:
                      currentLocationContacts?.storeManagerContact?.phone || "",
                    storeManagerContactEmail:
                      currentLocationContacts?.storeManagerContact?.email || "",
                    storeManagerContactPhoneType:
                      currentLocationContacts?.storeManagerContact?.phoneType ||
                      "",

                    // --- Upload Images/Videos ---
                    uploadImagesOrVideos: {
                      imageOfFullSmoodiSetUp:
                        freshStoredData?.locationNotes?.fullSetup || [],
                      setupFreezerDoorPhoto:
                        freshStoredData?.locationNotes?.freezerOpen || [],
                      machineBackPhoto:
                        freshStoredData?.locationNotes?.backOfMachine || [],
                      machineEntranceViewPhoto:
                        freshStoredData?.locationNotes?.relativeToEntrance ||
                        [],
                      storeWalkthroughVideo:
                        freshStoredData?.locationNotes?.video || [],
                      getStatusCheckImage: allStatusCheckImages || [],
                    },

                    // --- Additional Notes ---
                    notes: (freshStoredData?.locationNotes?.notes || []).filter(
                      (a: any) => a.categoryId,
                    ),
                  };

                  // Optionally, send to backend:
                  setSubmitting(true);
                  const response = await completeInstallationJob(
                    jobId,
                    $inputData,
                  );

                  if (response?.isSuccess) {
                    toast.success("Job updated successfully");
                    setShowJobCompleted(true);
                    //localStorage.removeItem(INSTALLATION_JOB_STORAGE_KEY);
                    fetchInstallationJob(jobId);
                    setCurrentStepWithStorage(
                      InstalltionFormSteps.PICTURES_AND_VIDEO,
                    ); // move to preview step
                  } else {
                    toast.error(response.message);
                  }
                  setSubmitting(false);
                  setIsFormSubmitted(false);
                }}
              >
                {({ handleSubmit, setFieldValue, values }) => {
                  return (
                    <form onSubmit={handleSubmit} autoComplete="off">
                      {currentStep !== InstalltionFormSteps.SUBMIT && (
                        <div className="active-step-label">
                          Step {currentStepIndex + 1} of{" "}
                          {installationJobStepsRequired.length} –{" "}
                          {
                            installationJobStepsRequired[currentStepIndex]
                              ?.label
                          }
                        </div>
                      )}
                      {/* {currentStep !== InstalltionFormSteps.SUBMIT && (
                    <div className="stepper-container">
                      {steps.map((step, index) => {
                        const isCompleted = index < currentStepIndex;
                        const isActive = index === currentStepIndex;

                        return (
                          <div key={step.id} className="step-wrapper">
                            <div
                              className={`step-circle ${
                                isCompleted ? "completed" : ""
                              } ${isActive ? "active" : ""}`}
                            >
                              {isCompleted ? "✓" : index + 1}
                            </div>

                            <div className="step-label">{step.label}</div>
                            {index !== steps.length - 1 && (
                              <div className="step-line" />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )} */}

                      {/* Step components */}
                      {currentStep === InstalltionFormSteps.CHOOSE_LOCATION && (
                        <LocationSelector
                          locationDB={locationsDB}
                          onLocationChange={handleLocationChange}
                          setCurrentStep={setCurrentStepWithStorage}
                          InstalltionFormSteps={InstalltionFormSteps}
                          jobStatus={installationJobDBStatus}
                          setStoredData={setStoredData}
                          installationJobDB={installationJobDB}
                        />
                      )}

                      {currentStep === InstalltionFormSteps.MACHINE_SETUP && (
                        <MachineSetup
                          locationDB={locationsDB}
                          machinesDB={machinesDB}
                          setCurrentStep={setCurrentStepWithStorage}
                          storedData={storedData}
                          jobStatus={installationJobDBStatus}
                          installationJobDB={installationJobDB}
                          jobId={jobId}
                          isFormSubmitted={isFormSubmitted}
                          setIsFormSubmitted={setIsFormSubmitted}
                        />
                      )}

                      {currentStep === InstalltionFormSteps.MACHINE_DATA && (
                        <MachineData
                          machinesDB={machinesDB}
                          setCurrentStep={setCurrentStepWithStorage}
                          setSelectedMachineForTicket={
                            setSelectedMachineForTicket
                          }
                          jobStatus={installationJobDBStatus}
                          installationJobDB={installationJobDB}
                          storedData={storedData}
                          blendersDB={blendersDB}
                          tubeShieldsDB={tubeShieldsDB}
                          cupHolderDB={cupHolderDB}
                          ledRingsDB={ledRingsDB}
                          boardsDB={boardsDB}
                          installationJobStepsRequired={
                            installationJobStepsRequired
                          }
                          refetchBlenders={refetchBlenders}
                          refetchBoards={refetchBoards}
                          refetchTubeShields={refetchTubeShields}
                          refetchCupHolders={refetchCupHolders}
                          refetchLedRings={refetchLedRings}
                        />
                      )}

                      {currentStep === InstalltionFormSteps.CREATE_TICKET && (
                        <CreateTicket
                          setCurrentStep={setCurrentStepWithStorage}
                          issueRepotedDB={issueRepotedDB}
                          ticketTypes={ticketTypes}
                          machineServicesDB={machineServicesDB}
                          diagnosisDB={diagnosis}
                          InstalltionFormSteps={InstalltionFormSteps}
                          selectedMachineForTicket={selectedMachineForTicket}
                          installationJobDB={installationJobDB}
                          jobStatus={installationJobDBStatus}
                          installationJobStepsRequired={
                            installationJobStepsRequired
                          }
                        />
                      )}

                      {currentStep ===
                        InstalltionFormSteps.LOCATION_INFORMATION && (
                        <LocationInformation
                          setCurrentStep={setCurrentStepWithStorage}
                          InstalltionFormSteps={InstalltionFormSteps}
                          headerDB={headerDB}
                          flavors={flavors}
                          materialDB={materialDB}
                          installationJobDB={installationJobDB}
                          jobStatus={installationJobDBStatus}
                          fetchVideos={fetchVideos}
                          videoOptions={videoOptions}
                          installationJobStepsRequired={
                            installationJobStepsRequired
                          }
                        />
                      )}

                      {currentStep ===
                        InstalltionFormSteps.LOCATION_CONTACTS && (
                        <LocationContacts
                          contactTypeDB={contactTypeDB}
                          setCurrentStep={setCurrentStepWithStorage}
                          InstalltionFormSteps={InstalltionFormSteps}
                          storedData={storedData}
                          locationsDB={locationsDB}
                          installationJobDB={installationJobDB}
                          jobStatus={installationJobDBStatus}
                          installationJobStepsRequired={
                            installationJobStepsRequired
                          }
                          isFormSubmitted={isFormSubmitted}
                          setIsFormSubmitted={setIsFormSubmitted}
                        />
                      )}

                      {currentStep ===
                        InstalltionFormSteps.PICTURES_AND_VIDEO && (
                        <PictureAndVideo
                          jobId={jobId}
                          values={values}
                          setFieldValue={setFieldValue}
                          setCurrentStep={setCurrentStepWithStorage}
                          InstalltionFormSteps={InstalltionFormSteps}
                          categoriesDB={categoriesDB}
                          installationJobDB={installationJobDB}
                          jobStatus={installationJobDBStatus}
                          isFormSubmitted={isFormSubmitted}
                          setIsFormSubmitted={setIsFormSubmitted}
                          installationJobStepsRequired={
                            installationJobStepsRequired
                          }
                          fetchInstallationJob={fetchInstallationJob}
                          setToast={setToast}
                          setShowJobCompleted={setShowJobCompleted}
                        />
                      )}

                      {currentStep === InstalltionFormSteps.SUBMIT &&
                      !id &&
                      installationJobDBStatus === "Completed" ? (
                        <InstallationJobPreview
                          installationJobDB={installationJobDB}
                        />
                      ) : (
                        ""
                      )}

                      {id &&
                      installationJobDB !== null &&
                      installationJobDBStatus === "Completed" ? (
                        <InstallationJobPreview
                          installationJobDB={installationJobDB}
                        />
                      ) : (
                        ""
                      )}

                      {id && installationJobDB === null ? (
                        <div className="not-found-section">
                          <div className="not-found-icon">
                            <svg
                              width="80"
                              height="80"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              stroke-width="2"
                            >
                              <circle cx="12" cy="12" r="10"></circle>
                              <line x1="12" y1="8" x2="12" y2="12"></line>
                              <line x1="12" y1="16" x2="12.01" y2="16"></line>
                            </svg>
                          </div>
                          <h3>Record Not Found</h3>
                          <p className="not-found-message">
                            We couldn't find any matching records. Please try
                            adjusting your search criteria.
                          </p>
                          <div>
                            <button
                              className="btn btn-primary mt-5"
                              onClick={() => navigate("/installation-job")}
                            >
                              Click here to create new
                            </button>
                          </div>
                        </div>
                      ) : (
                        ""
                      )}
                    </form>
                  );
                }}
              </Formik>
            </div>
          </div>
        )}
        {loading && (
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-center">
                <div className="spinner-border text-primary" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
      <Toaster
        position="bottom-left"
        reverseOrder={false}
        gutter={8}
        containerClassName=""
        containerStyle={{}}
        toastOptions={{
          className: "",
          duration: 3000,
        }}
      />
    </>
  );
}

export default InstallationFormOutline;
